import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { ExerciseLibrary } from '../components/ExerciseLibrary';
import { RecoveryCalculator } from '../components/RecoveryCalculator';
import { Download, FileText, Calendar, CheckCircle2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

export const Resources = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    condition: '',
    preferredDate: '',
    message: ''
  });

  const guides = [
    {
      title: 'Rotator Cuff Exercise Program',
      description: 'Comprehensive 18-exercise program with detailed instructions and guidelines',
      pages: 18,
      icon: FileText,
      filename: 'rotator-cuff-exercise-program',
      url: '/pdfs/rotator-cuff-exercise-program.html'
    },
    {
      title: 'Pre-Surgery Preparation Guide',
      description: 'Complete checklist for preparing for shoulder surgery',
      pages: 12,
      icon: FileText,
      filename: 'pre-surgery-guide',
      url: null
    },
    {
      title: 'Post-Surgery Recovery Guide',
      description: 'Week-by-week recovery instructions and milestones',
      pages: 20,
      icon: FileText,
      filename: 'post-surgery-recovery',
      url: null
    },
    {
      title: 'Frozen Shoulder Home Treatment',
      description: 'Self-care strategies and exercises for frozen shoulder',
      pages: 10,
      icon: FileText,
      filename: 'frozen-shoulder-treatment',
      url: null
    }
  ];

  const handleDownload = (guide) => {
    // If our branded guide exists, open it in new window for download/print
    if (guide.url) {
      window.open(guide.url, '_blank');
      toast({
        title: 'Guide Opened',
        description: `${guide.title} - Use Ctrl+P (Cmd+P on Mac) to save as PDF`,
      });
    } else {
      // Coming soon guides
      toast({
        title: 'Coming Soon',
        description: `${guide.title} will be available soon. Contact us for more information.`,
      });
    }
    
    console.log(`Guide requested: ${guide.filename}`);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // In real implementation, send to backend/CRM
    toast({
      title: 'Appointment Request Received!',
      description: 'Our team will contact you within 24 hours to confirm your appointment.',
    });
    
    // Reset form
    setFormData({
      name: '',
      email: '',
      phone: '',
      condition: '',
      preferredDate: '',
      message: ''
    });
    
    console.log('Appointment request:', formData);
  };

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="bg-gradient-to-br from-orange-50 to-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Patient Resources & Tools
            </h1>
            <p className="text-xl text-gray-600">
              Everything you need for successful shoulder care and recovery
            </p>
          </div>
        </div>
      </section>

      {/* Downloadable Guides */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Downloadable Patient Guides
              </h2>
              <p className="text-lg text-gray-600">
                Free comprehensive guides for your shoulder care journey
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {guides.map((guide, index) => {
                const Icon = guide.icon;
                return (
                  <Card key={index} className="hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <div className="flex items-start justify-between mb-2">
                        <Icon className="w-12 h-12 text-medical-orange" />
                        <span className="text-sm text-gray-500">{guide.pages} pages</span>
                      </div>
                      <CardTitle className="text-xl">{guide.title}</CardTitle>
                      <CardDescription>{guide.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Button
                        onClick={() => handleDownload(guide)}
                        className="w-full bg-medical-orange hover:bg-orange-600 text-white"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        {guide.url ? 'View & Download PDF' : 'Coming Soon'}
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Exercise Library */}
      <section className="bg-gray-50">
        <ExerciseLibrary />
      </section>

      {/* Recovery Calculator */}
      <RecoveryCalculator />

      {/* Appointment Booking */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Book an Appointment
              </h2>
              <p className="text-lg text-gray-600">
                Schedule a consultation with Dr Deepthi Nandan Reddy
              </p>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-6 h-6 text-medical-orange" />
                  Request an Appointment
                </CardTitle>
                <CardDescription>
                  Fill out the form below and we'll contact you to confirm your appointment
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                        Full Name *
                      </label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        placeholder="Enter your name"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                        Email *
                      </label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                        Phone Number *
                      </label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        placeholder="+91 XXXXX XXXXX"
                      />
                    </div>
                    <div>
                      <label htmlFor="preferredDate" className="block text-sm font-medium text-gray-700 mb-1">
                        Preferred Date
                      </label>
                      <Input
                        id="preferredDate"
                        name="preferredDate"
                        type="date"
                        value={formData.preferredDate}
                        onChange={handleInputChange}
                        min={new Date().toISOString().split('T')[0]}
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="condition" className="block text-sm font-medium text-gray-700 mb-1">
                      Shoulder Condition
                    </label>
                    <Input
                      id="condition"
                      name="condition"
                      type="text"
                      value={formData.condition}
                      onChange={handleInputChange}
                      placeholder="e.g., Rotator cuff pain, Frozen shoulder"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                      Additional Information
                    </label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={4}
                      placeholder="Tell us about your symptoms, previous treatments, or any questions you have..."
                    />
                  </div>

                  <div className="bg-blue-50 p-4 rounded-lg">
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div className="text-sm text-gray-700">
                        <p className="font-medium mb-1">What happens next?</p>
                        <ul className="space-y-1 text-gray-600">
                          <li>• We'll review your request within 24 hours</li>
                          <li>• Our team will call you to confirm the appointment</li>
                          <li>• You'll receive appointment details via email/SMS</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-medical-orange hover:bg-orange-600 text-white"
                    size="lg"
                  >
                    <Calendar className="w-5 h-5 mr-2" />
                    Request Appointment
                  </Button>

                  <p className="text-xs text-center text-gray-500">
                    By submitting this form, you agree to be contacted by our team regarding your appointment.
                  </p>
                </form>
              </CardContent>
            </Card>

            {/* Alternative Contact */}
            <div className="mt-8 text-center">
              <p className="text-gray-600 mb-4">Prefer to book by phone?</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="tel:+919959964567">
                  <Button variant="outline" size="lg">
                    Call +91 99599 64567
                  </Button>
                </a>
                <a href="https://wa.me/919959964567" target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" size="lg" className="bg-green-50 hover:bg-green-100 border-green-200">
                    WhatsApp Us
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};